"use client";

import { arrayMoveImmutable } from "array-move";
import {
  Check,
  Loader2,
  Move,
  Pause,
  Play,
  Plus,
  Tag,
  Upload,
  X,
} from "lucide-react";
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SortableList, { SortableItem } from "react-easy-sort";
import { toast } from "sonner";
import { AppImage as Image } from "@/components/ui/app-image";
import { Button } from "@/components/ui/button";
import { HlsVideo } from "@/components/ui/hls-video";
import { SoundToggleButton } from "@/components/ui/media-sound-button";
import { clampFeedMediaAspectRatio } from "@/lib/aspect-ratios";
import {
  checkAspectRatio,
  formatAspectRatio,
  loadImage,
  loadVideo,
} from "@/lib/image-utils";
import { createClientLogger } from "@/lib/logging/client";
import {
  type MediaAsset,
  type NormalizedMediaAsset,
  normalizeMediaArray,
} from "@/lib/media";
import {
  encodeImageInWorker,
  type MediaUploadScope,
  uploadBinary,
} from "@/lib/media/upload-pipeline";
import { createRandomId, createShortRandomId } from "@/lib/random-id";
import { FeedStyleMediaView } from "./feed-style-media-view";
import {
  type CroppedImageResult,
  type CropQueueItem,
  ImageCropModal,
} from "./image-crop-modal";
import { moderateImageFile } from "./image-moderation";

const logger = createClientLogger("components/image-upload/image-upload");

export interface UploadedMedia extends NormalizedMediaAsset {
  isUploading?: boolean;
  error?: string;
}

export type MediaUploadState = {
  isUploading: boolean;
  hasErrors: boolean;
  isReviewing?: boolean;
  isCropping?: boolean;
  pendingCount: number;
  errorCount: number;
  readyCount: number;
  totalCount: number;
  reviewCount?: number;
  reviewedCount?: number;
};

export const EMPTY_MEDIA_UPLOAD_STATE: MediaUploadState = {
  isUploading: false,
  hasErrors: false,
  isReviewing: false,
  isCropping: false,
  pendingCount: 0,
  errorCount: 0,
  readyCount: 0,
  totalCount: 0,
  reviewCount: 0,
  reviewedCount: 0,
};

// Re-exported for existing importers; the type now lives with the pipeline.
export type { MediaUploadScope };

export interface MediaUploadProps {
  maxImages?: number;
  onImagesChange?: (images: UploadedMedia[]) => void;
  onUploadingChange?: (isUploading: boolean) => void;
  onUploadStateChange?: (state: MediaUploadState) => void;
  initialImages?: MediaAsset[];
  className?: string;
  showReorderHint?: boolean;
  /** The "Your Photos (n/max)" heading above the grid. */
  showCountHeading?: boolean;
  disabled?: boolean;
  aspect?: number;
  /** Fixed display frame; does not require or crop the source aspect ratio. */
  previewAspect?: number;
  enableCropping?: boolean;
  acceptVideos?: boolean;
  maxImageSize?: number;
  maxVideoSize?: number;
  uploadScope?: MediaUploadScope;
  showSizeRequirements?: boolean;
  /** Base layer for the crop dialog when this uploader is inside a modal. */
  dialogLayer?: number;
  /**
   * When provided, ready image tiles show a "tag people" affordance that calls
   * this with the image id. Used by the post composer; omit elsewhere.
   */
  onTagImage?: (imageId: string) => void;
  /** People-tag count per image id, for the tile badge. */
  tagCounts?: Record<string, number>;
  /**
   * Native gallery picker. When provided (native platforms), the add tile
   * invokes it instead of the web file input and feeds the result into the
   * shared upload pipeline. Receives the number of remaining slots.
   */
  nativePicker?: (limit: number) => Promise<File[]>;
  /**
   * Layout variant. "grid" (default) is the square upload grid used for
   * profile pictures etc. "feed" is the Instagram-style large 4:5 preview +
   * filmstrip used by the post composer. Both share the same pipeline.
   */
  variant?: "grid" | "feed";
}

type MediaReviewState = {
  isReviewing: boolean;
  totalCount: number;
  completedCount: number;
};

const DEFAULT_UPLOAD_SCOPE: MediaUploadScope = {
  domain: "profiles",
  slot: "images",
};

const MIN_TILE_ASPECT_RATIO = 9 / 16;
const MAX_TILE_ASPECT_RATIO = 16 / 9;
const EMPTY_MEDIA_REVIEW_STATE: MediaReviewState = {
  isReviewing: false,
  totalCount: 0,
  completedCount: 0,
};

/** Moderation calls run a few at a time so a multi-select isn't serial. */
const MODERATION_CONCURRENCY = 3;

const MEDIA_TILE_SELECT_STYLE: CSSProperties = {
  WebkitTouchCallout: "none",
  WebkitUserSelect: "none",
  KhtmlUserSelect: "none",
  MozUserSelect: "none",
  msUserSelect: "none",
  userSelect: "none",
};

const createMediaSyncSignature = (items: UploadedMedia[]) =>
  JSON.stringify(
    items.map((item) => ({
      id: item.id,
      key: item.key,
      url: item.url,
      order: item.order,
      kind: item.kind,
      mimeType: item.mimeType,
      posterKey: item.posterKey,
      posterUrl: item.poster?.url,
      isUploading: !!item.isUploading,
      error: item.error ?? null,
    })),
  );

const createPendingItem = (
  id: string,
  order: number,
  kind: "image" | "video",
): UploadedMedia => ({
  id,
  provider: "r2",
  key: "",
  url: "",
  kind,
  mimeType: kind === "video" ? "video/mp4" : "image/avif",
  order,
  isUploading: true,
});

const toUploadedMedia = (item: UploadedMedia): UploadedMedia => ({ ...item });

const summarizeMediaUploadState = (
  items: UploadedMedia[],
  options?: {
    reviewState?: MediaReviewState;
    isCropping?: boolean;
  },
): MediaUploadState => {
  const pendingCount = items.filter((item) => item.isUploading).length;
  const errorCount = items.filter((item) => !!item.error).length;
  const readyCount = items.filter((item) => item.url && !item.error).length;
  const reviewState = options?.reviewState ?? EMPTY_MEDIA_REVIEW_STATE;
  const isReviewing = reviewState.isReviewing;
  const isCropping = options?.isCropping ?? false;

  return {
    ...EMPTY_MEDIA_UPLOAD_STATE,
    isUploading: pendingCount > 0 || isReviewing || isCropping,
    hasErrors: errorCount > 0,
    isReviewing,
    isCropping,
    pendingCount,
    errorCount,
    readyCount,
    totalCount: items.length,
    reviewCount: reviewState.totalCount,
    reviewedCount: reviewState.completedCount,
  };
};

const clampTileAspectRatio = (ratio?: number) => {
  if (!ratio || !Number.isFinite(ratio) || ratio <= 0) {
    return 1;
  }

  return Math.min(
    MAX_TILE_ASPECT_RATIO,
    Math.max(MIN_TILE_ASPECT_RATIO, ratio),
  );
};

const createTileAspectStyle = (ratio?: number): CSSProperties => ({
  aspectRatio: `${clampTileAspectRatio(ratio).toFixed(4)} / 1`,
});

const createFileSelectionSignature = (file: File) =>
  `${file.name}:${file.size}:${file.type}:${file.lastModified}`;

export function ImageUpload({
  maxImages = 6,
  onImagesChange,
  onUploadingChange,
  onUploadStateChange,
  initialImages = [],
  className = "",
  showReorderHint = true,
  showCountHeading = true,
  disabled = false,
  aspect,
  previewAspect,
  enableCropping = !!aspect,
  acceptVideos = true,
  maxImageSize = 20 * 1024 * 1024,
  maxVideoSize = 250 * 1024 * 1024,
  uploadScope,
  showSizeRequirements = false,
  dialogLayer,
  onTagImage,
  tagCounts,
  nativePicker,
  variant = "grid",
}: MediaUploadProps) {
  const normalizedInitial = useMemo(
    () =>
      normalizeMediaArray(initialImages).map((img, index) => ({
        ...img,
        order: img.order ?? index,
      })),
    [initialImages],
  );
  const normalizedInitialSignature = useMemo(
    () => createMediaSyncSignature(normalizedInitial),
    [normalizedInitial],
  );

  const [images, setImages] = useState<UploadedMedia[]>(normalizedInitial);
  const [mediaAspectRatios, setMediaAspectRatios] = useState<
    Record<string, number>
  >({});
  const [feedAspectRatio, setFeedAspectRatio] = useState<number | undefined>(
    undefined,
  );
  const [reviewState, setReviewState] = useState<MediaReviewState>(
    EMPTY_MEDIA_REVIEW_STATE,
  );
  const [isDragOver, setIsDragOver] = useState(false);
  const [cropQueue, setCropQueue] = useState<CropQueueItem[]>([]);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [playingVideoIds, setPlayingVideoIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [isMuted, setIsMuted] = useState(true);
  const draftIdRef = useRef(createShortRandomId(12));
  const lastAppliedInitialSignatureRef = useRef(normalizedInitialSignature);
  const uploadState = useMemo(
    () =>
      summarizeMediaUploadState(images, {
        reviewState,
        isCropping: isCropModalOpen,
      }),
    [images, reviewState, isCropModalOpen],
  );
  const effectiveAspect =
    aspect ?? (variant === "feed" ? feedAspectRatio : undefined);

  // Use ref to store callbacks and avoid effect dependency churn.
  const onImagesChangeRef = useRef(onImagesChange);
  onImagesChangeRef.current = onImagesChange;

  const onUploadingChangeRef = useRef(onUploadingChange);
  onUploadingChangeRef.current = onUploadingChange;

  const onUploadStateChangeRef = useRef(onUploadStateChange);
  onUploadStateChangeRef.current = onUploadStateChange;

  useEffect(() => {
    if (normalizedInitialSignature === lastAppliedInitialSignatureRef.current) {
      return;
    }

    setImages((prev) => {
      // Never overwrite state while uploads are in progress — the parent may
      // feed back a normalised list that excludes pending items, which would
      // drop items mid-upload and cause "item not found" errors.
      if (
        prev.some((img) => img.isUploading) ||
        reviewState.isReviewing ||
        isCropModalOpen
      ) {
        return prev;
      }

      lastAppliedInitialSignatureRef.current = normalizedInitialSignature;
      const previousSignature = createMediaSyncSignature(prev);
      if (previousSignature === normalizedInitialSignature) {
        return prev;
      }
      return normalizedInitial;
    });
  }, [
    normalizedInitial,
    normalizedInitialSignature,
    reviewState.isReviewing,
    isCropModalOpen,
  ]);

  // Notify parent whenever media changes.
  useEffect(() => {
    onImagesChangeRef.current?.(images.map(toUploadedMedia));
  }, [images]);

  // Track uploading status for parent forms.
  useEffect(() => {
    onUploadingChangeRef.current?.(uploadState.isUploading);
    onUploadStateChangeRef.current?.(uploadState);
  }, [uploadState]);

  const effectiveScope = uploadScope ?? DEFAULT_UPLOAD_SCOPE;
  const cropQueueRef = useRef<CropQueueItem[]>([]);
  const cropCompleteGuardRef = useRef(false);
  const cropUploadOrdersRef = useRef(new Map<string, number>());
  const activeFileSelectionsRef = useRef(new Set<string>());
  const videoPreviewRefs = useRef(new Map<string, HTMLVideoElement>());

  // Ref to always hold the latest images array (avoids stale closure in uploadFiles)
  const imagesRef = useRef(images);
  imagesRef.current = images;

  const revokeCropQueueUrls = useCallback((items: CropQueueItem[]) => {
    for (const item of items) {
      URL.revokeObjectURL(item.originalUrl);
    }
  }, []);

  const clearCropQueue = () => {
    setCropQueue((prev) => {
      revokeCropQueueUrls(prev);
      return [];
    });
  };

  useEffect(() => {
    cropQueueRef.current = cropQueue;
  }, [cropQueue]);

  useEffect(
    () => () => {
      revokeCropQueueUrls(cropQueueRef.current);
    },
    [revokeCropQueueUrls],
  );

  const setImageError = (localId: string, error: string) => {
    setImages((prev) =>
      prev.map((img) =>
        img.id === localId ? { ...img, isUploading: false, error } : img,
      ),
    );
  };

  const replaceImage = (localId: string, nextImage: UploadedMedia) => {
    setImages((prev) =>
      prev.map((img) => (img.id === localId ? nextImage : img)),
    );
    setMediaAspectRatios((prev) => {
      const ratio = prev[localId];
      if (!ratio || localId === nextImage.id) {
        return prev;
      }

      const next = { ...prev };
      delete next[localId];
      next[nextImage.id] = ratio;
      return next;
    });
  };

  const rememberMediaAspectRatio = useCallback((id: string, ratio: number) => {
    if (!Number.isFinite(ratio) || ratio <= 0) {
      return;
    }

    setMediaAspectRatios((prev) => {
      const previous = prev[id];
      if (previous && Math.abs(previous - ratio) < 0.01) {
        return prev;
      }

      return { ...prev, [id]: ratio };
    });
  }, []);

  const setVideoPreviewPlaying = useCallback(
    (id: string, isPlaying: boolean) => {
      setPlayingVideoIds((prev) => {
        const next = new Set(prev);
        if (isPlaying) {
          next.add(id);
        } else {
          next.delete(id);
        }
        return next;
      });
    },
    [],
  );

  const toggleVideoPreview = useCallback(
    async (id: string) => {
      const video = videoPreviewRefs.current.get(id);
      if (!video) return;

      if (video.paused) {
        try {
          await video.play();
          setVideoPreviewPlaying(id, true);
        } catch (error) {
          logger.warn("[ImageUpload] could not play video preview", error);
        }
        return;
      }

      video.pause();
      setVideoPreviewPlaying(id, false);
    },
    [setVideoPreviewPlaying],
  );

  const getPreferredTileAspectRatio = useCallback(
    (id?: string) => {
      if (previewAspect !== undefined) return previewAspect;
      if (effectiveAspect !== undefined) {
        return effectiveAspect;
      }

      return (id ? mediaAspectRatios[id] : undefined) ?? 1;
    },
    [mediaAspectRatios, effectiveAspect, previewAspect],
  );

  const resolveFileDimensions = async (file: File) => {
    try {
      if (file.type.startsWith("video/")) {
        const { width, height } = await loadVideo(file);
        return width > 0 && height > 0 ? { width, height } : undefined;
      }

      const image = await loadImage(file);
      const width = image.naturalWidth || image.width;
      const height = image.naturalHeight || image.height;
      return width > 0 && height > 0 ? { width, height } : undefined;
    } catch (error) {
      logger.warn("[ImageUpload] failed to resolve media aspect ratio", {
        fileName: file.name,
        error,
      });
      return undefined;
    }
  };

  const processAndUploadFile = async (input: {
    file: File;
    order: number;
    localId: string;
    dimensions?: { width: number; height: number };
  }): Promise<UploadedMedia> => {
    const isVideo = input.file.type.startsWith("video/");
    const encoded = isVideo ? null : await encodeImageInWorker(input.file);
    const uploaded = await uploadBinary({
      file: encoded?.file ?? input.file,
      mimeType: encoded?.mimeType ?? input.file.type,
      scope: effectiveScope,
      draftId: draftIdRef.current,
    });
    if (!uploaded.asset)
      throw new Error("Media processing did not return an asset");
    const normalized = normalizeMediaArray([
      { ...uploaded.asset, order: input.order },
    ])[0];
    if (!normalized) throw new Error("Invalid processed media");
    return { ...normalized, order: input.order, isUploading: false };
  };

  // Handle crop modal completion (guarded against double-fire from Dialog.onOpenChange)
  const handleCropComplete = async (croppedImages: CroppedImageResult[]) => {
    if (cropCompleteGuardRef.current) {
      return;
    }
    cropCompleteGuardRef.current = true;

    setIsCropModalOpen(false);
    clearCropQueue();

    const files = croppedImages.map((item) => item.croppedFile);
    const localIds = croppedImages.map((item) => item.id);
    const orders = localIds.map(
      (localId, index) =>
        cropUploadOrdersRef.current.get(localId) ??
        imagesRef.current.length + index,
    );

    try {
      await uploadFiles(files, { localIds, orders });
    } finally {
      for (const localId of localIds) {
        cropUploadOrdersRef.current.delete(localId);
      }
      cropCompleteGuardRef.current = false;
    }

    for (const croppedImg of croppedImages) {
      if (croppedImg.croppedUrl) {
        URL.revokeObjectURL(croppedImg.croppedUrl);
      }
    }
  };

  // Handle crop modal cancellation
  const handleCropCancel = async (appliedCrops: CroppedImageResult[]) => {
    // If cropComplete is already running, skip entirely to prevent double-fire
    if (cropCompleteGuardRef.current) {
      return;
    }

    const queuedIds = cropQueueRef.current.map((item) => item.id);

    if (appliedCrops.length > 0) {
      await handleCropComplete(appliedCrops);
      for (const queuedId of queuedIds) {
        cropUploadOrdersRef.current.delete(queuedId);
      }
      return;
    }

    for (const queuedId of queuedIds) {
      cropUploadOrdersRef.current.delete(queuedId);
    }
    setIsCropModalOpen(false);
    clearCropQueue();
  };

  // Upload files to R2 using signed targets.
  const uploadFiles = async (
    filesToUpload: File[],
    options?: {
      localIds?: string[];
      orders?: number[];
      forcedKind?: "image" | "video";
    },
  ) => {
    // Build pending items synchronously using the ref for the latest image count.
    // IMPORTANT: Do NOT create these inside setImages() — React 18 defers the
    // updater callback, so newImages would still be [] when the for-loop starts.
    const baseLength = imagesRef.current.length;
    const pendingInputs = filesToUpload.map((file, index) => {
      const localId =
        options?.localIds?.[index] ?? `${Date.now()}-${Math.random()}-${index}`;
      const kind =
        options?.forcedKind ??
        (file.type.startsWith("video/") ? "video" : "image");
      const order = options?.orders?.[index] ?? baseLength + index;
      return { file, localId, kind, order };
    });
    const resolvedDimensions = await Promise.all(
      pendingInputs.map((input) => resolveFileDimensions(input.file)),
    );
    const newImages: UploadedMedia[] = pendingInputs.map((input) =>
      createPendingItem(input.localId, input.order, input.kind),
    );

    setMediaAspectRatios((prev) => {
      let next = prev;
      for (let index = 0; index < pendingInputs.length; index += 1) {
        const dimensions = resolvedDimensions[index];
        if (!dimensions) continue;
        const ratio = dimensions.width / dimensions.height;
        if (next === prev) {
          next = { ...prev };
        }
        next[pendingInputs[index].localId] = ratio;
      }
      return next;
    });

    setImages((prev) =>
      [...prev, ...newImages].sort(
        (first, second) => first.order - second.order,
      ),
    );

    for (let i = 0; i < newImages.length; i++) {
      const pending = newImages[i];
      const file = filesToUpload[i];

      try {
        const uploaded = await processAndUploadFile({
          file,
          order: pending.order,
          localId: pending.id,
          dimensions: resolvedDimensions[i],
        });

        replaceImage(pending.id, uploaded);
      } catch (error) {
        logger.error("[ImageUpload] uploadFiles: upload error", {
          localId: pending.id,
          file_type: file.type,
          file_size_bytes: file.size,
          error,
        });
        const errorMessage =
          error instanceof Error ? error.message : "Upload failed";
        setImageError(pending.id, errorMessage);
        toast.error(`Upload failed for "${file.name}": ${errorMessage}`);
      }
    }
  };

  const handleFileSelect = async (files: FileList | File[] | null) => {
    if (!files || disabled || uploadState.isUploading) return;

    const selectedFiles = Array.from(files);
    const selectedSignatures: string[] = [];
    const fileArray: File[] = [];

    for (const file of selectedFiles) {
      const signature = createFileSelectionSignature(file);
      if (activeFileSelectionsRef.current.has(signature)) {
        continue;
      }

      activeFileSelectionsRef.current.add(signature);
      selectedSignatures.push(signature);
      fileArray.push(file);
    }

    if (fileArray.length === 0) return;

    try {
      const validFiles: File[] = [];
      const rejectedFiles: { file: File; reason: string }[] = [];

      // Validate each file (file type and size)
      for (const file of fileArray) {
        const validation = validateMediaFile(
          file,
          acceptVideos,
          maxImageSize,
          maxVideoSize,
        );
        if ("error" in validation) {
          rejectedFiles.push({ file, reason: validation.error });
        } else {
          validFiles.push(file);
        }
      }

      // Show toast notifications for rejected files
      if (rejectedFiles.length > 0) {
        const uniqueReasons = [...new Set(rejectedFiles.map((r) => r.reason))];
        if (uniqueReasons.length === 1) {
          toast.error(
            `${rejectedFiles.length} file${rejectedFiles.length > 1 ? "s" : ""} rejected: ${uniqueReasons[0]}`,
          );
        } else {
          toast.error(
            `${rejectedFiles.length} files rejected due to various errors`,
          );
          uniqueReasons.forEach((reason) => {
            const count = rejectedFiles.filter(
              (r) => r.reason === reason,
            ).length;
            toast.error(`${count} file${count > 1 ? "s" : ""}: ${reason}`);
          });
        }
      }

      const availableSlots = maxImages - images.length;
      const filesToProcess = validFiles.slice(0, availableSlots);

      // Show warning if some valid files were skipped due to slot limits
      if (validFiles.length > availableSlots && availableSlots > 0) {
        const skippedCount = validFiles.length - availableSlots;
        toast.warning(
          `${skippedCount} file${skippedCount > 1 ? "s" : ""} skipped - maximum ${maxImages} photos allowed`,
        );
      } else if (validFiles.length > 0 && availableSlots === 0) {
        toast.warning(
          `Cannot add more photos - maximum ${maxImages} photos allowed`,
        );
      }

      if (filesToProcess.length === 0) return;

      // Every await from here on happens behind the "preparing media" tiles,
      // so the picker never hands back to a screen that looks like nothing
      // happened. The state is cleared in the outer finally.
      setReviewState({
        isReviewing: true,
        totalCount: filesToProcess.length,
        completedCount: 0,
      });

      // Moderation is a decode + downscale + round trip per file; running a
      // few at a time keeps a multi-select from waiting on a serial queue.
      const moderationOutcomes: (File | null)[] = new Array(
        filesToProcess.length,
      ).fill(null);
      let moderationCursor = 0;
      let moderationCompleted = 0;

      const runModerationWorker = async () => {
        while (true) {
          const index = moderationCursor;
          moderationCursor += 1;
          if (index >= filesToProcess.length) return;

          const file = filesToProcess[index];
          const moderation = await moderateImageFile(file);
          if (moderation.ok) {
            moderationOutcomes[index] = file;
          } else {
            toast.error(moderation.reason);
          }

          moderationCompleted += 1;
          setReviewState({
            isReviewing: true,
            totalCount: filesToProcess.length,
            completedCount: moderationCompleted,
          });
        }
      };

      await Promise.all(
        Array.from(
          {
            length: Math.min(MODERATION_CONCURRENCY, filesToProcess.length),
          },
          runModerationWorker,
        ),
      );

      const moderatedFiles = moderationOutcomes.filter(
        (file): file is File => file !== null,
      );

      if (moderatedFiles.length === 0) return;

      // Feed posts inherit the first selected item's ratio (within feed-safe
      // 4:5–1.91:1 bounds). Every image is then cropped through the same
      // movable frame; videos show that frame but keep their original binary.
      let targetAspect = effectiveAspect;
      if (targetAspect === undefined && variant === "feed" && enableCropping) {
        const firstDimensions = await resolveFileDimensions(moderatedFiles[0]);
        targetAspect = clampFeedMediaAspectRatio(
          firstDimensions
            ? firstDimensions.width / firstDimensions.height
            : undefined,
        );
        setFeedAspectRatio(targetAspect);
      }

      // Check aspect ratio if required
      if (targetAspect !== undefined) {
        if (enableCropping) {
          const newCropQueue: CropQueueItem[] = [];
          const baseOrder = imagesRef.current.length;

          moderatedFiles.forEach((file, index) => {
            const localId = createRandomId();
            const order = baseOrder + index;
            const kind = file.type.startsWith("video/") ? "video" : "image";

            cropUploadOrdersRef.current.set(localId, order);
            newCropQueue.push({
              id: localId,
              file,
              originalUrl: URL.createObjectURL(file),
              kind,
            });
          });

          if (newCropQueue.length > 0) {
            setCropQueue(newCropQueue);
            setIsCropModalOpen(true);
          }

          return;
        }

        // Validate aspect ratio and reject non-conforming images
        const aspectValidFiles: File[] = [];
        const aspectInvalidFiles: Array<{
          file: File;
          actualAspect: number;
        }> = [];

        for (const file of moderatedFiles) {
          if (file.type.startsWith("video/")) {
            aspectValidFiles.push(file);
            continue;
          }

          try {
            const aspectCheck = await checkAspectRatio(file, targetAspect);
            if (aspectCheck.passes) {
              aspectValidFiles.push(file);
            } else {
              aspectInvalidFiles.push({
                file,
                actualAspect: aspectCheck.actualAspect,
              });
            }
          } catch (error) {
            logger.error("Error checking aspect ratio:", error);
            toast.error(`Failed to check aspect ratio for "${file.name}"`);
          }
        }

        // Show error for images that don't match aspect ratio
        if (aspectInvalidFiles.length > 0) {
          const formattedAspect = formatAspectRatio(targetAspect);
          if (aspectInvalidFiles.length === 1) {
            const item = aspectInvalidFiles[0];
            const actualFormatted = formatAspectRatio(item.actualAspect);
            toast.error(
              `Image must have a ${formattedAspect} aspect ratio. Current image is ${actualFormatted}.`,
            );
          } else {
            toast.error(
              `${aspectInvalidFiles.length} images don't match the required aspect ratio of ${formattedAspect}`,
            );
          }
        }

        if (aspectValidFiles.length > 0) {
          setReviewState(EMPTY_MEDIA_REVIEW_STATE);
          await uploadFiles(aspectValidFiles);
        }
        return;
      }

      setReviewState(EMPTY_MEDIA_REVIEW_STATE);
      await uploadFiles(moderatedFiles);
    } finally {
      // Cleared here rather than right after moderation so the placeholder
      // tiles stay up until the cropper is mounted — clearing early leaves a
      // frame where the picker has closed and nothing on screen has changed.
      setReviewState(EMPTY_MEDIA_REVIEW_STATE);
      for (const signature of selectedSignatures) {
        activeFileSelectionsRef.current.delete(signature);
      }
    }
  };

  /**
   * The native picker copies each chosen asset out of the photo library before
   * it resolves — seconds for a few large videos. Flag the review state up
   * front so the grid shows a placeholder instead of an unchanged screen;
   * handleFileSelect takes ownership of the flag from there.
   */
  const openNativePicker = nativePicker
    ? async (limit: number) => {
        setReviewState({
          isReviewing: true,
          totalCount: 0,
          completedCount: 0,
        });

        try {
          const files = await nativePicker(limit);
          if (files.length === 0) {
            setReviewState(EMPTY_MEDIA_REVIEW_STATE);
          }
          return files;
        } catch (error) {
          setReviewState(EMPTY_MEDIA_REVIEW_STATE);
          throw error;
        }
      }
    : undefined;

  const handleDragOver = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    if (uploadState.isUploading) return;
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    if (uploadState.isUploading) return;
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOver(false);
    if (uploadState.isUploading) return;
    void handleFileSelect(e.dataTransfer.files);
  };

  const removeImage = (id: string) => {
    if (disabled) return;
    const updatedImages = images.filter((img) => img.id !== id);
    setImages(updatedImages);
    videoPreviewRefs.current.delete(id);
    setVideoPreviewPlaying(id, false);
    setMediaAspectRatios((prev) => {
      if (!(id in prev)) {
        return prev;
      }

      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const onSortEnd = (oldIndex: number, newIndex: number) => {
    if (disabled) return;
    setImages((prevImages) => {
      const reorderedImages = arrayMoveImmutable(
        prevImages,
        oldIndex,
        newIndex,
      ).map((img, index) => ({
        ...img,
        order: index,
      }));
      return reorderedImages;
    });
  };

  const uploadedImages = images.filter((img) => img.url && !img.error);
  const reviewTotal = uploadState.reviewCount ?? 0;
  const reviewPosition = Math.min(
    (uploadState.reviewedCount ?? 0) + 1,
    Math.max(reviewTotal, 1),
  );
  const reviewLabel = uploadState.isReviewing
    ? reviewTotal > 1
      ? `Preparing ${reviewPosition} of ${reviewTotal}…`
      : "Preparing your media…"
    : null;
  const busyMessage =
    reviewLabel ??
    (uploadState.isCropping
      ? "Finish media review before saving."
      : uploadState.pendingCount > 0
        ? "Uploads in progress. Wait for them to finish before saving."
        : null);

  // Calculate available slots for upload tiles
  const availableSlots = maxImages - images.length;
  // One placeholder per selected file, so tapping "Add" visibly reserves the
  // slots the cropper is about to fill.
  const reviewTileCount = uploadState.isReviewing
    ? Math.min(Math.max(reviewTotal, 1), Math.max(availableSlots, 0))
    : 0;
  const showUploadTiles = availableSlots > 0 && !disabled;

  return (
    <>
      <style jsx>{`
        .dragged {
          transform: rotate(5deg);
          box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 10px 10px -5px rgb(0 0 0 / 0.04);
          z-index: 10;
        }
        .dragged img {
          pointer-events: none;
        }
      `}</style>
      <div className={`space-y-4 ${className}`}>
        {variant === "feed" ? (
          <FeedStyleMediaView
            images={images}
            maxImages={maxImages}
            disabled={disabled}
            acceptVideos={acceptVideos}
            uploadState={uploadState}
            onSortEnd={onSortEnd}
            onRemove={removeImage}
            onSelectFiles={handleFileSelect}
            nativePicker={openNativePicker}
            onTagImage={onTagImage}
            tagCounts={tagCounts}
            aspect={previewAspect ?? effectiveAspect}
          />
        ) : (
          /* Image Grid with Integrated Upload */
          <div className="space-y-4">
            {showCountHeading ? (
              <h4 className="font-medium text-text-primary">
                Your {acceptVideos ? "Media" : "Photos"} (
                {uploadedImages.length}/{maxImages})
              </h4>
            ) : null}

            <section
              className={`relative grid grid-cols-3 items-start gap-4 ${
                showUploadTiles ? "min-h-[120px]" : ""
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              aria-label={
                acceptVideos ? "Media upload grid" : "Photo upload grid"
              }
            >
              {/* Existing Images */}
              <SortableList
                onSortEnd={onSortEnd}
                className="contents"
                draggedItemClassName="dragged"
              >
                {images.map((image, index) => (
                  <SortableItem key={image.id}>
                    <div
                      className="group relative cursor-grab touch-manipulation overflow-hidden rounded-lg bg-surface shadow-lg select-none active:cursor-grabbing"
                      style={{
                        ...MEDIA_TILE_SELECT_STYLE,
                        ...createTileAspectStyle(
                          getPreferredTileAspectRatio(image.id),
                        ),
                      }}
                    >
                      {/* Image/Video Preview */}
                      <div className="relative h-full w-full">
                        {image.url ? (
                          image.kind === "video" ? (
                            <HlsVideo
                              ref={(node) => {
                                if (node) {
                                  videoPreviewRefs.current.set(image.id, node);
                                } else {
                                  videoPreviewRefs.current.delete(image.id);
                                }
                              }}
                              src={image.url}
                              className="h-full w-full object-cover"
                              playsInline
                              muted={isMuted}
                              loop
                              preload="metadata"
                              poster={image.poster?.url}
                              onLoadedMetadata={(event) => {
                                const video = event.currentTarget;
                                if (video.videoWidth && video.videoHeight) {
                                  rememberMediaAspectRatio(
                                    image.id,
                                    video.videoWidth / video.videoHeight,
                                  );
                                }
                              }}
                              onPlay={() =>
                                setVideoPreviewPlaying(image.id, true)
                              }
                              onPause={() =>
                                setVideoPreviewPlaying(image.id, false)
                              }
                            />
                          ) : (
                            <Image
                              src={image.url}
                              alt={`Photo ${index + 1}`}
                              fill
                              className="object-cover"
                              onLoad={(event) => {
                                const loadedImage = event.currentTarget;
                                if (
                                  loadedImage.naturalWidth &&
                                  loadedImage.naturalHeight
                                ) {
                                  rememberMediaAspectRatio(
                                    image.id,
                                    loadedImage.naturalWidth /
                                      loadedImage.naturalHeight,
                                  );
                                }
                              }}
                            />
                          )
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-surface-alt">
                            <Upload className="h-8 w-8 text-text-secondary" />
                          </div>
                        )}

                        {image.isUploading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                            <Loader2 className="h-8 w-8 animate-spin text-white" />
                          </div>
                        )}

                        {/* Always visible controls */}
                        <div className="absolute inset-0">
                          {/* Drag indicator - center */}
                          {image.kind !== "video" && (
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                              <div className="flex items-center gap-2 rounded-full bg-black/70 px-3 py-1 text-sm font-medium text-white">
                                <Move className="h-4 w-4" aria-hidden="true" />
                                Drag to reorder
                              </div>
                            </div>
                          )}

                          {image.kind === "video" &&
                          image.url &&
                          !image.isUploading &&
                          !image.error ? (
                            <>
                              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    void toggleVideoPreview(image.id);
                                  }}
                                  className="pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full bg-black/70 text-white shadow-lg ring-1 ring-white/20 backdrop-blur transition hover:scale-105 hover:bg-black/80"
                                  aria-label={
                                    playingVideoIds.has(image.id)
                                      ? "Pause video preview"
                                      : "Play video preview"
                                  }
                                >
                                  {playingVideoIds.has(image.id) ? (
                                    <Pause
                                      className="h-5 w-5"
                                      aria-hidden="true"
                                    />
                                  ) : (
                                    <Play
                                      className="ml-0.5 h-5 w-5"
                                      aria-hidden="true"
                                    />
                                  )}
                                </button>
                              </div>
                              <SoundToggleButton
                                muted={isMuted}
                                onToggle={() => setIsMuted((prev) => !prev)}
                              />
                            </>
                          ) : null}

                          {/* Position indicator - top left */}
                          <div className="absolute top-2 left-2">
                            <div className="rounded-full bg-black/70 px-2 py-1 text-xs font-medium text-white">
                              {index + 1}
                            </div>
                          </div>

                          {/* Remove button - top right */}
                          {!disabled && (
                            <div className="absolute top-2 right-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                aria-label={`Remove ${image.kind} ${index + 1}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeImage(image.id);
                                }}
                                className="h-8 w-8 rounded-full bg-black/70 p-0 text-white hover:bg-error"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          )}

                          {/* Status indicator - bottom left */}
                          <div className="absolute bottom-2 left-2">
                            {image.error ? (
                              <div className="rounded-full bg-error px-2 py-1 text-xs text-white">
                                Error
                              </div>
                            ) : image.isUploading ? (
                              <div className="rounded-full bg-accent px-2 py-1 text-xs text-white">
                                Uploading
                              </div>
                            ) : image.url ? (
                              <div className="rounded-full bg-green-500 p-1 text-white">
                                <Check className="h-3 w-3" aria-hidden="true" />
                              </div>
                            ) : null}
                          </div>

                          {/* Tag people - bottom right (post composer only, images only) */}
                          {onTagImage &&
                          !disabled &&
                          image.url &&
                          !image.isUploading &&
                          !image.error &&
                          image.kind === "image" ? (
                            <div className="absolute right-2 bottom-2">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onTagImage(image.id);
                                }}
                                className="flex items-center gap-1 rounded-full bg-black/70 px-2 py-1 text-xs font-medium text-white transition hover:bg-black/85"
                                aria-label="Tag people"
                              >
                                <Tag className="h-3.5 w-3.5" />
                                {tagCounts?.[image.id]
                                  ? tagCounts[image.id]
                                  : "Tag"}
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </SortableItem>
                ))}
              </SortableList>

              {/* Placeholder tiles while selected media is being prepared */}
              {Array.from({ length: reviewTileCount }, (_, i) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: transient placeholders with no unique id
                  key={`media-preparing-${i}`}
                  className="relative overflow-hidden rounded-lg border border-border bg-surface-alt shadow-lg"
                  style={createTileAspectStyle(
                    previewAspect ?? effectiveAspect ?? 1,
                  )}
                >
                  <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-surface-alt via-surface to-surface-alt" />
                  {i === 0 ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-3 text-center">
                      <Loader2
                        className="h-5 w-5 animate-spin text-text-secondary"
                        aria-hidden="true"
                      />
                      <p className="text-[11px] leading-tight font-medium text-text-secondary">
                        {reviewLabel}
                      </p>
                    </div>
                  ) : null}
                </div>
              ))}

              {/* Upload Tiles */}
              {showUploadTiles &&
                Array.from(
                  { length: Math.max(availableSlots - reviewTileCount, 0) },
                  (_, i) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: static empty slots with no unique id
                      key={`upload-slot-empty-${i}`}
                      className={`group relative cursor-pointer overflow-hidden rounded-lg border-2 border-dashed shadow-lg transition-all duration-300 ${
                        isDragOver
                          ? "border-accent bg-accent/10 hover:border-accent"
                          : "border-border hover:border-accent hover:bg-surface-alt"
                      } bg-surface`}
                      style={createTileAspectStyle(
                        previewAspect ?? effectiveAspect ?? 1,
                      )}
                    >
                      {openNativePicker ? (
                        <button
                          type="button"
                          onClick={() => {
                            if (
                              disabled ||
                              images.length >= maxImages ||
                              uploadState.isUploading
                            ) {
                              return;
                            }
                            void openNativePicker(availableSlots).then(
                              (files) => {
                                if (files.length > 0) {
                                  void handleFileSelect(files);
                                }
                              },
                            );
                          }}
                          disabled={
                            disabled ||
                            images.length >= maxImages ||
                            uploadState.isUploading
                          }
                          className="absolute inset-0 z-10 cursor-pointer opacity-0"
                          aria-label={acceptVideos ? "Add media" : "Add photo"}
                        />
                      ) : (
                        <input
                          type="file"
                          multiple
                          accept={acceptVideos ? "image/*,video/*" : "image/*"}
                          onChange={(e) => {
                            const input = e.currentTarget;
                            const selectedFiles = input.files;
                            void handleFileSelect(selectedFiles).finally(() => {
                              input.value = "";
                            });
                          }}
                          className="absolute inset-0 z-10 cursor-pointer opacity-0"
                          disabled={
                            disabled ||
                            images.length >= maxImages ||
                            uploadState.isUploading
                          }
                          aria-label={acceptVideos ? "Add media" : "Add photo"}
                        />
                      )}
                      <div className="flex h-full w-full flex-col items-center justify-center p-4 text-center">
                        <div className="mb-3 rounded-full bg-surface-alt p-3 transition-colors group-hover:bg-accent/10">
                          <Plus className="h-6 w-6 text-text-secondary" />
                        </div>
                        <p className="text-xs font-medium text-text-secondary">
                          {acceptVideos ? "Add Media" : "Add Photo"}
                        </p>
                      </div>
                    </div>
                  ),
                )}
            </section>

            {showSizeRequirements && (
              <div className="text-center">
                <p className="mt-1 text-xs text-text-secondary">
                  {acceptVideos
                    ? `Supports images up to ${Math.floor(maxImageSize / (1024 * 1024))}MB and videos up to ${Math.floor(maxVideoSize / (1024 * 1024))}MB`
                    : `Supports images up to ${Math.floor(maxImageSize / (1024 * 1024))}MB each`}
                </p>
              </div>
            )}

            {busyMessage && (
              <div
                aria-live="polite"
                className="flex items-center justify-center gap-2 text-xs text-text-secondary"
              >
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                <span className="max-w-full truncate">{busyMessage}</span>
              </div>
            )}

            {!uploadState.isUploading && uploadState.hasErrors && (
              <p className="text-center text-xs text-error">
                Remove failed uploads and try again before saving.
              </p>
            )}

            {showReorderHint && uploadedImages.length > 1 && !disabled && (
              <p className="text-center text-xs text-text-secondary">
                Drag {acceptVideos ? "media" : "photos"} to reorder • First{" "}
                {acceptVideos ? "item" : "photo"} will be your main profile
                picture
              </p>
            )}
          </div>
        )}
      </div>

      {/* Image Crop Modal */}
      <ImageCropModal
        images={cropQueue}
        aspect={effectiveAspect}
        onComplete={handleCropComplete}
        onCancel={handleCropCancel}
        isOpen={isCropModalOpen}
        layer={dialogLayer}
      />
    </>
  );
}

function validateMediaFile(
  file: File,
  acceptVideos: boolean,
  maxImageSize: number,
  maxVideoSize: number,
): { valid: true } | { error: string } {
  const isVideo = file.type.startsWith("video/");

  if (isVideo) {
    if (!acceptVideos) {
      return {
        error: `"${file.name}" is a video file. Only images are accepted.`,
      };
    }

    const allowedVideoTypes = [
      "video/mp4",
      "video/quicktime",
      "video/webm",
      "video/3gpp",
    ];

    if (!allowedVideoTypes.includes(file.type)) {
      return {
        error: `"${file.name}" is not a supported video format. Use MP4, MOV, or WebM.`,
      };
    }

    if (file.size > maxVideoSize) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
      const maxLimitMB = Math.floor(maxVideoSize / (1024 * 1024));
      return {
        error: `"${file.name}" is too large (${fileSizeMB}MB). Maximum video size is ${maxLimitMB}MB.`,
      };
    }

    return { valid: true };
  }

  // For images, use the existing validation
  return validateImageFile(file, maxImageSize);
}

function validateImageFile(
  file: File,
  maxImageSize: number,
): { valid: true } | { error: string } {
  // List of allowed MIME types (including HEIC for iOS)
  const allowedMimeTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/heic",
    "image/heif",
    "image/avif",
    "image/bmp",
    "image/tiff",
  ];

  // List of allowed file extensions
  const allowedExtensions = [
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".webp",
    ".heic",
    ".heif",
    ".avif",
    ".bmp",
    ".tiff",
    ".tif",
  ];

  // Get file extension
  const fileName = file.name.toLowerCase();
  const fileExtension = fileName.substring(fileName.lastIndexOf("."));

  // Validate file type using both MIME type and extension
  const fileType = file.type || "";
  const hasValidMimeType =
    fileType && allowedMimeTypes.includes(fileType.toLowerCase());
  const hasValidExtension = allowedExtensions.includes(fileExtension);

  // If MIME type is missing or generic, rely on extension
  const isValidImageType =
    hasValidMimeType ||
    ((!fileType || fileType === "application/octet-stream") &&
      hasValidExtension);

  if (!isValidImageType) {
    // Provide specific error messages
    if (!fileType || fileType === "application/octet-stream") {
      return {
        error: `"${file.name}" has an unsupported file type. Please use JPG, PNG, GIF, WebP, or HEIC images.`,
      };
    }

    return {
      error: `"${file.name}" (${fileType}) is not a supported image format. Please use JPG, PNG, GIF, WebP, or HEIC images.`,
    };
  }

  // Validate file size
  if (file.size > maxImageSize) {
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    const maxLimitMB = Math.floor(maxImageSize / (1024 * 1024));
    return {
      error: `"${file.name}" is too large (${fileSizeMB}MB). Maximum file size is ${maxLimitMB}MB.`,
    };
  }

  // Check for suspiciously small files (likely corrupted or not real images)
  if (file.size < 100) {
    return {
      error: `"${file.name}" appears to be corrupted or is not a valid image file.`,
    };
  }

  return { valid: true };
}
